import ChartSkeleton from '@/app/components/feedback/ChartSkeleton';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useCan } from '@/app/casl/AbilityContext';
import { RESOURCES } from '@/app/casl/resources';
import { AdminAction } from '@/app/models/AdminAction';
import PageShell from '@/app/components/layout/PageShell';
import StatusBadge from '@/app/modules/table/components/StatusBadge';
import MetronicCard from '@/app/components/layout/MetronicCard';

import { KTSVG as KtSvg } from '../../../_metronic/helpers';
import { DASHBOARD_PAGE_URL } from '../../../api/core/links';
import { ManagerManagementEndpoints } from '../../../api/endpoints/ManagerManagementEndpoints';
import { formatPriceToBRL } from '../../helpers/formatPriceToBRL';
import DataTableBody from '../../modules/table/components/DataTableBody';
import DataTableControls from '../../modules/table/components/DataTableControls';
import DataTablePagination from '../../modules/table/components/DataTablePagination';
import { DataTable } from '../../modules/table/DataTable';
import SummaryCard from '../accountant-space/components/SummaryCard';
import BillStatusTabNav from './components/BillStatusTabNav';
import EmptyBillList from './components/EmptyBillList';
import InstallmentDetailsModal from './components/InstallmentDetailsModal';
import ManagerBillsChart from './components/ManagerBillsChart';
import UploadPaymentDocModal from './components/UploadPaymentDocModal';
import { type FilterOption, buildPeriodOptions } from './core/_consts';
import {
  getManagerCommunities,
  getManagerDashboardChart,
  getManagerDashboardMetrics,
  getManagerPointsOfSale,
} from './core/_requests';

import { type PageLink } from '../../../_metronic/layout/core';
import { type DataTableColumnDef } from '../../models/DataTableColumnDef';
import { type InstallmentDto } from '../../models/InstallmentDto';

dayjs.locale('pt-br');

const breadcrumbs: Array<PageLink> = [
  {
    title: 'Dashboard',
    path: DASHBOARD_PAGE_URL,
    isSeparator: false,
    isActive: false,
  },
  { title: '', path: '', isSeparator: true, isActive: false },
  {
    title: 'Espaço Gestor',
    path: '/espaco-gestor',
    isSeparator: false,
    isActive: false,
  },
  { title: '', path: '', isSeparator: true, isActive: false },
];

const periodOptions: FilterOption[] = buildPeriodOptions();

type FiltersState = {
  communityId: string;
  posId: string;
  period: string;
};

const formatCurrencyCompact = (value: number) => {
  if (value >= 1_000_000) return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `R$ ${(value / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(value);
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

function ManagerSpaceDashboardPage() {
  const canChangeDashboard = useCan(RESOURCES.ESPACO_GESTOR_DASHBOARD, AdminAction.ALTERAR);
  const canExportDashboard = useCan(RESOURCES.ESPACO_GESTOR_DASHBOARD, AdminAction.EXPORTAR);
  const [filters, setFilters] = useState<FiltersState>({
    communityId: '',
    posId: '',
    period: periodOptions[0].value,
  });

  const [uploadModal, setUploadModal] = useState<{
    show: boolean;
    installmentId: string;
  }>({ show: false, installmentId: '' });

  const [detailsModal, setDetailsModal] = useState<{
    show: boolean;
    installmentId: string;
  }>({ show: false, installmentId: '' });

  const { data: communitiesResponse } = useQuery({
    queryKey: ['manager-communities'],

    queryFn: () => getManagerCommunities(),
  });

  const communities = useMemo(() => communitiesResponse?.data ?? [], [communitiesResponse?.data]);

  useEffect(() => {
    if (communities.length > 0 && !filters.communityId) {
      setFilters((prev) => ({ ...prev, communityId: communities[0].id ?? '' }));
    }
  }, [communities, filters.communityId]);

  const { data: posResponse } = useQuery({
    queryKey: ['manager-pos', filters.communityId],
    queryFn: () => getManagerPointsOfSale(filters.communityId),
    enabled: !!filters.communityId,
  });

  const pointsOfSale = useMemo(() => posResponse?.data ?? [], [posResponse?.data]);

  const {
    data: metricsResponse,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: ['manager-dashboard-metrics', filters.communityId, filters.posId, filters.period],

    queryFn: () =>
      getManagerDashboardMetrics({
        community_id: filters.communityId,
        pos_id: filters.posId || undefined,
        period: filters.period,
      }),

    enabled: !!filters.communityId,
    placeholderData: keepPreviousData,
  });
  const isRefreshingMetrics = isFetching && !isLoading;

  const {
    data: chartResponse,
    isLoading: isLoadingChart,
    isFetching: isFetchingChart,
  } = useQuery({
    queryKey: ['manager-dashboard-chart', filters.communityId, filters.posId, filters.period],

    queryFn: () =>
      getManagerDashboardChart({
        community_id: filters.communityId,
        pos_id: filters.posId || undefined,
        period: filters.period,
      }),

    enabled: !!filters.communityId,
    placeholderData: keepPreviousData,
  });
  const isRefreshingChart = isFetchingChart && !isLoadingChart;

  const metrics = useMemo(() => {
    const d = metricsResponse?.data;
    return {
      totalToPay: d?.total_to_pay ?? 0,
      totalToReceive: d?.total_to_receive ?? 0,
      netBalance: d?.net_balance ?? 0,
      overdueCount: d?.overdue_count ?? 0,
      overdueAmount: d?.overdue_amount ?? 0,
      dueThisMonthCount: d?.due_this_month_count ?? 0,
      overdueToPayCount: d?.overdue_to_pay_count ?? 0,
    };
  }, [metricsResponse?.data]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'to-pay',
        title: 'Contas a Pagar',
        value: formatCurrencyCompact(metrics.totalToPay),
        helper: `${formatNumber(metrics.overdueToPayCount)} vencidas`,
        icon: 'media/icons/duotune/finance/fin008.svg',
        theme: 'danger',
        helperColor: 'danger',
      },
      {
        id: 'to-receive',
        title: 'Contas a Receber',
        value: formatCurrencyCompact(metrics.totalToReceive),
        helper: `${formatNumber(metrics.dueThisMonthCount)} a vencer este mês`,
        icon: 'media/icons/duotune/finance/fin010.svg',
        theme: 'primary',
        helperColor: 'primary',
      },
      {
        id: 'balance',
        title: 'Saldo Líquido',
        value: formatCurrencyCompact(metrics.netBalance),
        helper: 'Receber - Pagar',
        icon: 'media/icons/duotune/general/gen016.svg',
        theme: metrics.netBalance >= 0 ? 'success' : 'warning',
        helperColor: metrics.netBalance >= 0 ? 'success' : 'warning',
      },
      {
        id: 'overdue',
        title: 'Vencidas',
        value: formatNumber(metrics.overdueCount),
        helper: formatCurrencyCompact(metrics.overdueAmount),
        icon: 'media/icons/duotune/general/gen050.svg',
        theme: 'warning',
        helperColor: 'warning',
      },
    ],
    [metrics],
  );

  const handleCommunityChange = (communityId: string) => {
    setFilters({ communityId, posId: '', period: filters.period });
  };

  const handleFilterChange = (key: keyof FiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const currentPeriodLabel = useMemo(
    () => periodOptions.find((o) => o.value === filters.period)?.label ?? '',
    [filters.period],
  );

  const buildBillColumns = (): DataTableColumnDef<InstallmentDto>[] => [
    {
      id: 'description',
      header: 'Descrição',
      accessorKey: 'description',

      cell: ({ row }) => (
        <div className="d-flex flex-column">
          <span className="text-gray-800 fw-semibold">
            {row.original.bill?.description || row.original.description || '-'}
          </span>
          <span className="text-gray-500 fs-8 fw-normal"> {row.original.bill?.obs ?? '--'}</span>
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Condomínio',
      accessorKey: 'bill.contact.name',

      enableSorting: false,
      cell: ({ row }) => (
        <div className="d-flex flex-column">
          <span className="text-gray-700 fw-semibold">{row.original.bill?.contact?.name || '-'}</span>
          <span className="text-gray-500 fs-8 fw-normal"> {row.original.bill?.point_of_sale?.locality ?? '--'}</span>
        </div>
      ),
    },
    {
      id: 'due_date',
      header: 'Vencimento',
      accessorKey: 'due_date',

      cell: ({ row }) => {
        const date = row.original.due_date;
        return <span className="text-gray-700 fw-semibold">{date ? dayjs(date).format('DD/MM/YYYY') : '-'}</span>;
      },
    },
    {
      id: 'gross_value',
      header: 'Valor',
      accessorKey: 'gross_value',

      cell: ({ row }) => (
        <span className="text-gray-800 fw-bold">{formatPriceToBRL(row.original.gross_value ?? 0)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status.value',

      cell: ({ row }) => {
        const status = row.original.status;
        const value = status?.value;
        switch (value) {
          case 'OPENED':
            return <StatusBadge label="Em aberto" tone="info" />;
          case 'PAID':
            return <StatusBadge label="Paga" tone="success" />;
          case 'DELAYED':
            return <StatusBadge label="Atrasada" tone="danger" />;
          case 'CANCELED':
            return <StatusBadge label="Cancelada" tone="secondary" />;
          default:
            return <StatusBadge label={status?.description || value || '-'} tone="warning" />;
        }
      },
    },
    {
      id: 'actions',
      header: 'Ações',

      cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-icon btn-light-info btn-sm"
            title="Ver detalhes"
            type="button"
            onClick={() =>
              setDetailsModal({
                show: true,
                installmentId: row.original.id ?? '',
              })
            }
          >
            <KtSvg className="svg-icon-2" path="media/icons/duotune/general/gen021.svg" />
            <span className="visually-hidden">Ver detalhes</span>
          </button>
          {canChangeDashboard ? (
            <button
              className="btn btn-icon btn-light-primary btn-sm"
              title="Enviar documento"
              type="button"
              onClick={() =>
                setUploadModal({
                  show: true,
                  installmentId: row.original.id ?? '',
                })
              }
            >
              <KtSvg className="svg-icon-2" path="media/icons/duotune/files/fil018.svg" />
              <span className="visually-hidden">Enviar documento</span>
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  const billColumns = useMemo(() => buildBillColumns(), [canChangeDashboard]);

  const tableFilter = useMemo(
    () => ({
      community_id: filters.communityId,
      pos_id: filters.posId || undefined,
      period: filters.period,
    }),
    [filters.communityId, filters.posId, filters.period],
  );

  return (
    <PageShell breadcrumbs={breadcrumbs} title="Dashboard">
      {/* Filters */}
      <div className="row g-4 mb-8">
        <div className="col-12">
          <MetronicCard className="card-flush shadow-sm" loading={isRefreshingMetrics}>
            <div className="card-body py-6">
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-6 gap-4">
                <div>
                  <h3 className="fw-bold text-gray-800 mb-1">Filtros de visualização</h3>
                  <div className="text-gray-500 fw-semibold">Ajuste os filtros para acompanhar suas contas</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span className="text-gray-600 fw-semibold">Período atual:</span>
                  <span className="badge badge-light-primary fs-base px-4 py-2">{currentPeriodLabel}</span>
                </div>
              </div>

              <div className="row g-4">
                <div className="col-12 col-md-4">
                  <label className="form-label fs-6 fw-semibold text-gray-700">Comunidade</label>
                  <select
                    className="form-select form-select-solid"
                    value={filters.communityId}
                    onChange={(e) => handleCommunityChange(e.target.value)}
                  >
                    {communities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label fs-6 fw-semibold text-gray-700">Ponto de Venda</label>
                  <select
                    className="form-select form-select-solid"
                    value={filters.posId}
                    onChange={(e) => handleFilterChange('posId', e.target.value)}
                  >
                    <option value="">Todos</option>
                    {pointsOfSale.map((pos) => (
                      <option key={pos.id} value={pos.id}>
                        {pos.locality}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label fs-6 fw-semibold text-gray-700">Período</label>
                  <select
                    className="form-select form-select-solid"
                    value={filters.period}
                    onChange={(e) => handleFilterChange('period', e.target.value)}
                  >
                    {periodOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </MetronicCard>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-6 mb-8">
        {summaryCards.map((card) => (
          <div key={card.id} className="col-12 col-md-6 col-xl-3">
            <SummaryCard
              helper={card.helper}
              helperColor={card.helperColor}
              icon={card.icon}
              initialLoading={isLoading}
              loading={isRefreshingMetrics}
              theme={card.theme}
              title={card.title}
              value={card.value}
            />
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="row mb-8">
        <div className="col-12">
          <MetronicCard className="card-flush shadow-sm" loading={isRefreshingChart}>
            <div className="card-header pt-6">
              <h3 className="card-title fw-bold text-gray-800">A Pagar vs A Receber</h3>
            </div>
            <div className="card-body pt-2 pb-6">
              {isLoadingChart ? <ChartSkeleton height={350} /> : <ManagerBillsChart data={chartResponse?.data} />}
            </div>
          </MetronicCard>
        </div>
      </div>

      {/* Bills to Pay Table */}
      <div className="row mb-8">
        <div className="col-12">
          <MetronicCard className="card-flush" loading={isRefreshingMetrics}>
            <div className="card-header pt-6">
              <h3 className="card-title fw-bold text-gray-800">Contas a Pagar</h3>
            </div>
            <div className="card-body py-4">
              {filters.communityId === '' && (
                <div className="alert alert-info">Selecione uma comunidade para visualizar as contas a pagar.</div>
              )}
              {filters.communityId !== '' && (
                <DataTable
                  columns={billColumns}
                  direction="asc"
                  endpoint={ManagerManagementEndpoints.listBillsToPay}
                  exportEndpoint={ManagerManagementEndpoints.billsToPayExport}
                  exportFilterLabels={{}}
                  filter={tableFilter}
                  isExportable={canExportDashboard}
                  isRefreshable={true}
                  placeHolder={<EmptyBillList />}
                  searchPlaceHolder="Buscar pela descrição ou contato"
                  sort="due_date"
                  tableKey="manager-bills-to-pay-table"
                  variant="list"
                >
                  <DataTableControls />
                  <BillStatusTabNav />
                  <DataTableBody showHeader={true} />
                  <DataTablePagination />
                </DataTable>
              )}
            </div>
          </MetronicCard>
        </div>
      </div>

      {/* Bills to Receive Table */}
      <div className="row mb-8">
        <div className="col-12">
          <MetronicCard className="card-flush" loading={isRefreshingMetrics}>
            <div className="card-header pt-6">
              <h3 className="card-title fw-bold text-gray-800">Contas a Receber</h3>
            </div>
            <div className="card-body py-4">
              {filters.communityId === '' && (
                <div className="alert alert-info">Selecione uma comunidade para visualizar as contas a receber.</div>
              )}
              {filters.communityId !== '' && (
                <DataTable
                  columns={billColumns}
                  direction="asc"
                  endpoint={ManagerManagementEndpoints.listBillsToReceive}
                  exportEndpoint={ManagerManagementEndpoints.billsToReceiveExport}
                  exportFilterLabels={{}}
                  filter={tableFilter}
                  isExportable={canExportDashboard}
                  isRefreshable={true}
                  placeHolder={<EmptyBillList />}
                  searchPlaceHolder="Buscar pela descrição ou contato"
                  sort="due_date"
                  tableKey="manager-bills-to-receive-table"
                  variant="list"
                >
                  <DataTableControls />
                  <BillStatusTabNav />
                  <DataTableBody showHeader={true} />
                  <DataTablePagination />
                </DataTable>
              )}
            </div>
          </MetronicCard>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadPaymentDocModal
        installmentId={uploadModal.installmentId}
        show={uploadModal.show}
        onClose={() => setUploadModal({ show: false, installmentId: '' })}
      />

      {/* Details Modal */}
      <InstallmentDetailsModal
        installmentId={detailsModal.installmentId}
        show={detailsModal.show}
        onClose={() => setDetailsModal({ show: false, installmentId: '' })}
      />
    </PageShell>
  );
}

export default ManagerSpaceDashboardPage;
